// src/components/Plumber/JobProgress.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Clock,
  User,
  Phone,
  Check,
  CheckCircle2,
  Navigation,
  MapPinned,
  Flag,
  Loader2,
  MessageCircle
} from "lucide-react";
import { doc, getDoc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { Button } from "../ui/Button";

export default function JobProgress() {
  const navigate = useNavigate();
  const {jobId} = useParams();

  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState("");

  const statusSteps = useMemo(
    () => [
      {
        status: "confirmed",
        label: "Confirmed",
        icon: CheckCircle2,
        description: "Job has been confirmed and scheduled",
      },
      {
        status: "on the way",
        label: "On the Way",
        icon: Navigation,
        description: "Heading to customer location",
      },
      {
        status: "arrived",
        label: "Arrived",
        icon: MapPinned,
        description: "Arrived at customer location",
      },
      {
        status: "completed",
        label: "Completed",
        icon: Flag,
        description: "Job successfully completed",
      },
    ],
    []
  );

  useEffect(() => {
    if (!jobId) {
      setError("Missing jobId");
      setLoading(false);
      return;
    }

    const jobRef = doc(db, "jobs", jobId);

    const unsubscribe = onSnapshot(
      jobRef,
      async (snap) => {
        if (!snap.exists()) {
          setError("Job not found");
          setJob(null);
          setLoading(false);
          return;
        }

        const jobData = { id: snap.id, ...snap.data() };
        setJob(jobData);

        try {
          if (jobData.customerId) {
            const customerRef = doc(db, "customers", jobData.customerId);
            const customerSnap = await getDoc(customerRef);

            if (customerSnap.exists()) {
              setCustomer({ id: customerSnap.id, ...customerSnap.data() });
            } else {
              setCustomer(null);
            }
          } else {
            setCustomer(null);
          }
        } catch (err) {
          console.error("Failed to fetch customer:", err);
          setCustomer(null);
        }

        setError("");
        setLoading(false);
      },
      (err) => {
        console.error("Job listener error:", err);
        setError("Failed to load job");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [jobId]);

  const progressStatus = job?.progressStatus || "confirmed";

  const safeStatusSteps = progressStatus === "cancelled" ? [] : statusSteps;

  const currentStepIndex =
    progressStatus === "cancelled"
      ? -1
      : Math.max(
          0,
          safeStatusSteps.findIndex((s) => s.status === progressStatus)
        );

  const getNextStatus = () => {
    if (progressStatus === "cancelled" || progressStatus === "completed") return null;
    if (currentStepIndex < safeStatusSteps.length - 1) {
      return safeStatusSteps[currentStepIndex + 1].status;
    }
    return null;
  };

  const nextStatus = getNextStatus();

  const handleStatusUpdate = async (newStatus) => {
    if (!job?.id || updating) return;

    try {
      setUpdating(true);
      setError("");

      const payload = {
        progressStatus: newStatus,
        updatedAt: serverTimestamp(),
      };

      if (newStatus === "completed") {
        payload.status = "completed";
        payload.completedAt = serverTimestamp();
      }

      await updateDoc(doc(db, "jobs", job.id), payload);

      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 2000);
    } catch (err) {
      console.error("Failed to update status:", err);
      setError("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const serviceTitle = job?.subcategoryId || "Service";
  const customerName = job?.customerName || customer?.name || "Customer";
  const customerPhone = customer?.phone || "";
  const locationText = job?.locationText || "No location provided";
  const budget = Number(job?.price || 0);

  const preferredDateText = job?.preferredDate
    ? new Date(job.preferredDate).toLocaleDateString()
    : "No date";
  const preferredTimeText = job?.preferredTime || "No time";

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-600">
          <Loader2 className="animate-spin" size={20} />
          <span>Loading job...</span>
        </div>
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
              <ArrowLeft size={24} />
            </button>
            <h2 className="text-[#1E3A8A]">Job Progress</h2>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-10">
          <div className="bg-white rounded-xl shadow-sm p-6 text-center text-red-600">
            {error}
          </div>
        </div>
      </div>
    );
  }

  const currentStep =
    progressStatus === "cancelled"
      ? {
          label: "Cancelled",
          description: "This job has been cancelled",
          icon: Flag,
        }
      : safeStatusSteps[currentStepIndex];

  const CurrentIcon = currentStep?.icon || CheckCircle2;
  
  const handleCallClick = async () => {
    if (!customerPhone) return;

    if (phoneRevealed) {
      window.location.href = `tel:${customerPhone}`;
      return;
    }

    setPhoneRevealed(true);

    try {
      await navigator.clipboard.writeText(customerPhone);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-[#1E3A8A]">Job Progress</h2>
        </div>
      </header>

      {/* Confirmation Toast */}
      {showConfirmation && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <CheckCircle2 size={20} />
            <span>Status updated successfully!</span>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {/* Job Details Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-start justify-between mb-4 gap-4">
            <div>
              <h3 className="text-xl text-[#1E3A8A] mb-2">{serviceTitle}</h3>

              {job?.categoryId && (
                <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm">
                  {job.categoryId}
                </span>
              )}
            </div>

            <div className="text-right">
              <div className="text-2xl text-[#006C35] mb-1">{budget} SAR</div>
            </div>
          </div>

          <p className="text-gray-600 mb-4">{job?.note || "No description provided"}</p>

          {/* Customer Information */}
          <div className="border-t border-gray-200 pt-4 mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Customer Information</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-sm">
                <User size={18} className="text-gray-400" />
                <span className="text-gray-700">{customerName}</span>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <Phone size={18} className="text-gray-400" />
                {customerPhone ? (
                  <a href={`tel:${customerPhone}`} className="text-[#006C35] hover:underline">
                    {customerPhone}
                  </a>
                ) : (
                  <span className="text-gray-500">No phone number</span>
                )}
              </div>
            </div>
          </div>

          {/* Job Schedule */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Schedule</h4>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 text-sm">
                <MapPin size={18} className="text-gray-400" />
                <span className="text-gray-600">{locationText}</span>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <Clock size={18} className="text-gray-400" />
                <span className="text-gray-600">
                  {preferredDateText} at {preferredTimeText}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Tracker */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg text-[#1E3A8A] mb-6">Job Status</h3>

          {/* Visual Progress Steps */}
          {progressStatus !== "cancelled" && (
            <div className="mb-8">
              <div className="flex items-center justify-between relative">
                <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 z-0">
                  <div
                    className="h-full bg-[#006C35] transition-all duration-500"
                    style={{
                      width: `${
                        safeStatusSteps.length > 1
                          ? (currentStepIndex / (safeStatusSteps.length - 1)) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>

                {safeStatusSteps.map((step, index) => {
                  const Icon = step.icon;
                  const isCompleted = index <= currentStepIndex;
                  const isCurrent = index === currentStepIndex;

                  return (
                    <div key={step.status} className="flex flex-col items-center relative z-10">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all duration-300 ${
                          isCompleted
                            ? "bg-[#006C35] text-white"
                            : "bg-gray-200 text-gray-400"
                        } ${isCurrent ? "ring-4 ring-green-100" : ""}`}
                      >
                        <Icon size={20} />
                      </div>

                      <span
                        className={`text-xs text-center max-w-20 ${
                          isCompleted ? "text-[#006C35] font-medium" : "text-gray-500"
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Current Status Info */}
          <div
            className={`rounded-lg p-4 mb-6 ${
              progressStatus === "cancelled"
                ? "bg-red-50"
                : "bg-linear-to-r from-green-50 to-blue-50"
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  progressStatus === "cancelled" ? "bg-red-600" : "bg-[#006C35]"
                }`}
              >
                <CurrentIcon size={16} className="text-white" />
              </div>

              <div>
                <div className="text-sm text-gray-600">Current Status</div>
                <div
                  className={`font-semibold ${
                    progressStatus === "cancelled" ? "text-red-700" : "text-[#1E3A8A]"
                  }`}
                >
                  {currentStep?.label}
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 ml-11">{currentStep?.description}</p>
          </div>

          {/* Status Update Buttons */}
          <div className="space-y-3">
            {nextStatus && (
              <Button
                onClick={() => handleStatusUpdate(nextStatus)}
                disabled={updating}
                className="w-full bg-[#006C35] hover:bg-[#005428] py-6 text-base disabled:opacity-60"
              >
                {(() => {
                  const nextStep = safeStatusSteps.find((s) => s.status === nextStatus);
                  const Icon = nextStep?.icon || CheckCircle2;

                  return (
                    <>
                      {updating ? (
                        <Loader2 size={20} className="mr-2 animate-spin" />
                      ) : (
                        <Icon size={20} className="mr-2" />
                      )}
                      Update to "{nextStep?.label}"
                    </>
                  );
                })()}
              </Button>
            )}

            {progressStatus === "completed" && (
              <div className="text-center py-4">
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-100 text-green-700 rounded-lg">
                  <CheckCircle2 size={20} />
                  <span className="font-medium">Job Completed Successfully!</span>
                </div>
                <p className="text-sm text-gray-500 mt-3">
                  Payment will be processed and added to your earnings.
                </p>
              </div>
            )}

            {progressStatus === "cancelled" && (
              <div className="text-center py-4">
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-red-100 text-red-700 rounded-lg">
                  <Flag size={20} />
                  <span className="font-medium">This job has been cancelled.</span>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="pt-4 border-t border-gray-200 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-[#006C35] text-[#006C35] rounded-lg hover:bg-green-50 transition-colors"
                >
                  <MessageCircle size={18} className="mr-2" />
                  Chat
                </Button>

                <Button
                  className="py-6 bg-[#006C35] hover:bg-[#005428] text-white relative"
                  onClick={handleCallClick}
                  disabled={!customerPhone}
                >
                  <Phone size={18} className="mr-2" />

                  <span className="truncate">
                    {phoneRevealed ? customerPhone : "Call"}
                  </span>

                  {copied && (
                    <span className="absolute -top-1 -right-1 text-[10px] bg-white text-[#006C35] px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                      <Check size={10} />
                      Copied
                    </span>
                  )}
                </Button>
              </div>

              <Button
                variant="outline"
                className="w-full py-6 border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={() => {
                  const lat = job?.location?.lat;
                  const lng = job?.location?.lng;

                  if (lat != null && lng != null) {
                    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
                  }
                }}
                disabled={job?.location?.lat == null || job?.location?.lng == null}
              >
                <MapPin size={18} className="mr-2" />
                Directions
              </Button>
            </div>
          </div>
        </div>

        {/* Additional Notes */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">Important Reminders</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Update your status regularly to keep the customer informed</li>
            <li>• Take before and after photos for quality assurance</li>
            <li>• Contact customer if you need any clarifications</li>
            <li>• Complete the job on time to maintain your rating</li>
          </ul>
        </div>
      </div>
    </div>
  );
}